import { type ReactNode, useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Box, Button, CircularProgress, Input, Stack, Tooltip } from "@mui/joy";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PersonIcon from "@mui/icons-material/Person";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import { theme } from "styles/theme";
import CustomTypography from "components/common/CustomTypography";
import CustomModal from "components/common/CustomModal";
import TabButtonList from "components/common/TabButtonList";
import { PATH } from "routes/paths";
import {
  ambassadorApi,
  clearAmbassadorToken,
  getAmbassadorToken,
  type AmbassadorStatsBucket,
} from "api/ambassadorApi";
import distancingLogo from "static/image/common/distancing_logo.png";
import CodeStatusChip, { StatusChip } from "pages/ambassador/CodeStatusChip";

type Tab = "daily" | "monthly";

const FALLBACK_ERROR_MESSAGE = "잠시 후 다시 시도해 주세요";

// 서버 검증과 동일 규칙 — 영문 소문자·숫자·하이픈·언더스코어 3~20자
const CODE_SLUG_REGEX = /^[a-z0-9_-]{3,20}$/;

// 앰버서더 환영 문구 후보 — 계약 전(시작 독려)/후(기여) 톤으로 나뉜다.
// 첫 문구는 계약 전후 공통 고정(브랜드 미션) — 진입 시 항상 이 문구부터 보이고, 이후 순환한다.
// "마음 돌봄"·"회복"·"공유"·"링크" 등 비공식·부적합 표현 금지.
// 계약 전에는 아직 앰버서더가 아니다 — 확정적 호칭 없이, 대의(마음 건강 기여)를 담은 기대 톤으로 쓴다.
// 계약 안내는 배너가 담당하므로 문구에서 계약을 직접 언급하지 않는다.
const WELCOME_MESSAGES_BEFORE_CONTRACT = [
  () => "건강한 정신 건강 문화를 만듭니다.",
  () => "곧 누군가의 마음 건강을 지키는 활동을 시작해요.",
  () => "디스턴싱이 필요한 분들에게 닿는 일, 함께 시작해요.",
  () => "누군가에게는 변화의 시작이 될 활동이 기다리고 있어요.",
  () => "디스턴싱과 함께 마음 건강을 전할 준비를 해봐요.",
];
const WELCOME_MESSAGES_AFTER_CONTRACT = [
  (_name: string) => "건강한 정신 건강 문화를 만듭니다.",
  (name: string) => `${name}님의 활동이 누군가의 마음 건강을 지키는 힘이 돼요.`,
  (name: string) =>
    `${name}님이 소개한 디스턴싱이 누군가에게는 변화의 시작이 돼요.`,
  (name: string) => `${name}님 덕분에 디스턴싱이 필요한 분들에게 닿고 있어요.`,
  (name: string) => `${name}님을 통해 누군가에게 큰 힘이되고 있어요.`,
  (name: string) =>
    `${name}님과 함께 더 많은 분들이 디스턴싱을 알아가고 있어요.`,
  (name: string) => `오늘도 ${name}님과 함께 건강한 마음을 전하고 있어요.`,
  (name: string) => `${name}님의 활동 하나하나가 큰 힘이 돼요.`,
];

// 환영 문구 — 첫 문구(브랜드 미션)로 시작해 30초마다 다음 문구로 순환, 400ms 페이드로 자연스럽게 전환한다.
function WelcomeMessage({
  name,
  contracted,
}: {
  name: string | null;
  contracted: boolean;
}) {
  const pool = contracted
    ? WELCOME_MESSAGES_AFTER_CONTRACT
    : WELCOME_MESSAGES_BEFORE_CONTRACT;
  // 시작 인덱스 고정 — 계약 전이든 후든 진입 시 가장 먼저 보이는 문구는 풀의 첫 문구다.
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setIndex((i) => i + 1);
        setVisible(true);
      }, 400);
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  return (
    <CustomTypography
      variant="Caption"
      sx={{
        // 문구별 줄 수 차이로 아래 카드가 오르내리지 않게 2줄 높이를 고정 확보
        minHeight: "40px",
        fontWeight: 500,
        color: theme.vars.palette.Content.Medium,
        opacity: visible ? 1 : 0,
        transition: "opacity 0.4s ease",
      }}
    >
      {pool[index % pool.length](name ?? "앰버서더")}
    </CustomTypography>
  );
}

// 레퍼럴 앰버서더(인플루언서) 성과 대시보드 — /with/:code 링크의 가입자/결제자/환불자를 total·일별·월별로 본다.
// 링크가 없으면 셀프 생성 UI 를, 계약 정보 미제출이면 입력 유도 배너를 보여준다.
// 토큰 만료(401)는 ambassadorAxios 인터셉터가 로그인 리다이렉트로 처리한다.
export default function AmbassadorDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [tab, setTab] = useState<Tab>("daily");
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  // 내 링크 풀 URL 복사 (예: https://distancing.im/with/CODE)
  const copyLink = async (code: string) => {
    const link = `${window.location.origin}/with/${code}`;
    try {
      await navigator.clipboard.writeText(link);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 1500);
    } catch (e) {
      // clipboard 미지원 브라우저 — 링크 텍스트가 select-all 이라 수동 복사 가능
    }
  };

  const hasToken = !!getAmbassadorToken();

  const { data, isLoading, isError, refetch } = useQuery(
    ["referral-ambassador-dashboard"],
    () => ambassadorApi.dashboard(),
    { enabled: hasToken, retry: false, refetchOnWindowFocus: false }
  );

  // 계약 정보 제출 여부 (미제출 배너용) — 파일까지 내려오지만 호출은 대시보드 진입 시 1회뿐이다.
  const { data: contractData } = useQuery(
    ["referral-ambassador-contract"],
    () => ambassadorApi.getContract(),
    { enabled: hasToken, retry: false, refetchOnWindowFocus: false }
  );

  if (!hasToken) {
    // 로그인 후 이 페이지로 복귀하도록 출발지를 state 로 전달한다 (로그인 페이지가 해석)
    return (
      <Navigate
        to={PATH.ambassadorLogin}
        replace
        state={{ from: location.pathname + location.search }}
      />
    );
  }

  const handleLogout = () => {
    clearAmbassadorToken();
    navigate(PATH.ambassadorLogin, { replace: true });
  };

  const result = data?.code === 0 ? data.result : undefined;
  const failed = isError || (data !== undefined && data.code !== 0);
  const buckets: AmbassadorStatsBucket[] =
    (tab === "daily" ? result?.daily : result?.monthly) ?? [];
  const contractSubmitted =
    contractData?.code === 0 && !!contractData.result?.type;
  // 계약 완료 = 어드민이 체결 확인 후 표시 — 링크 생성 게이트·상태 칩·환영 문구 풀 분기 기준
  const contractCompleted =
    contractData?.code === 0 && !!contractData.result?.completed;
  const showContractBanner =
    !!result && contractData?.code === 0 && !contractSubmitted;
  const showSettlement = tab === "monthly";

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        overflowY: "auto",
        backgroundColor: theme.vars.palette.Background.Level1,
      }}
    >
      <Box
        sx={{
          mx: "auto",
          width: "100%",
          maxWidth: "720px",
          px: "20px",
          py: "40px",
          // 푸터 로고를 화면(콘텐츠) 최하단에 붙이기 위한 컬럼 레이아웃
          display: "flex",
          flexDirection: "column",
          minHeight: "100%",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          {/* 브랜드 헤더 — "디스턴싱 앰버서더" 소속 표현 + 계약 상태 칩 (로고는 푸터에 은은하게) */}
          <Stack direction="row" alignItems="center" spacing={"8px"}>
            <CustomTypography
              variant="H3"
              sx={{
                fontWeight: 700,
                color: theme.vars.palette.Content.Strong,
              }}
            >
              {"디스턴싱 앰버서더"}
            </CustomTypography>
            {contractData?.code === 0 && (
              // 3단계: 계약 정보 입력 전 → 계약 전 → 활동 중(계약 완료).
              // 완료 전에만 클릭 시 계약 정보 페이지로 이동 — 활동 중 상태에서는 이동 없음
              <StatusChip
                positive={contractCompleted}
                label={
                  !contractSubmitted
                    ? "계약 정보 입력 전"
                    : contractCompleted
                      ? "활동 중"
                      : "계약 전"
                }
                onClick={
                  contractCompleted
                    ? undefined
                    : () => navigate(PATH.ambassadorContract)
                }
              />
            )}
          </Stack>
          <AccountMenu
            onLink={() => navigate(PATH.ambassadorLink)}
            onContract={() => navigate(PATH.ambassadorContract)}
            onLogout={handleLogout}
          />
        </Stack>

        {/* 환영 문구·링크 목록 — 계정 관리 버튼과 같은 행에 두지 않고 full width 로 노출 */}
        {result && (
          <Stack spacing={"4px"} sx={{ mt: "8px" }}>
            {/* 계약 여부 로드 후에 노출 — 풀이 중간에 바뀌며 문구가 튀는 것을 방지 */}
            {contractData?.code === 0 && (
              <WelcomeMessage
                name={result.name ?? null}
                contracted={contractCompleted}
              />
            )}
            {(result.codeInfos ?? []).map(({ code, isActive }) => (
              <Stack
                key={code}
                direction="row"
                alignItems="center"
                spacing={"8px"}
              >
                <CustomTypography
                  variant="Caption"
                  sx={{
                    fontWeight: 500,
                    color: isActive
                      ? theme.vars.palette.Content.Medium
                      : theme.vars.palette.Content.Weak,
                    userSelect: "all",
                    wordBreak: "break-all",
                  }}
                >
                  {`${window.location.origin}/with/${code}`}
                </CustomTypography>
                {/* 링크 복사 — 항상 노출, 복사 직후엔 체크 아이콘으로 피드백 */}
                {copiedCode === code ? (
                  <CheckRoundedIcon
                    sx={{
                      flexShrink: 0,
                      fontSize: "18px",
                      color: "#349434",
                    }}
                  />
                ) : (
                  <ContentCopyRoundedIcon
                    titleAccess="링크 복사"
                    onClick={() => copyLink(code)}
                    sx={{
                      flexShrink: 0,
                      fontSize: "18px",
                      color: theme.vars.palette.Content.Medium,
                      cursor: "pointer",
                    }}
                  />
                )}
                {/* 미승인(비활성) 코드는 상태 칩을 함께 표시 */}
                {!isActive && <CodeStatusChip isActive={false} />}
              </Stack>
            ))}
          </Stack>
        )}

        {isLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: "80px" }}>
            <CircularProgress color="neutral" />
          </Box>
        )}
        {failed && (
          <CustomTypography
            variant="Caption"
            sx={{
              mt: 5,
              textAlign: "center",
              fontWeight: 500,
              color: theme.vars.palette.Content.HighlightWithAlert,
            }}
          >
            {"데이터를 불러오지 못했어요. 잠시 후 다시 시도해 주세요."}
          </CustomTypography>
        )}

        {showContractBanner && (
          <Box
            onClick={() => navigate(PATH.ambassadorContract)}
            sx={{
              mt: 3,
              cursor: "pointer",
              borderRadius: "12px",
              backgroundColor: theme.vars.palette.Surface.SoftHighlight,
              px: "16px",
              py: "12px",
            }}
          >
            <CustomTypography
              variant="Caption"
              sx={{
                fontWeight: 500,
                color: theme.vars.palette.Content.Highlight,
              }}
            >
              {"계약 정보를 입력해 주세요 — 계약서 발송과 정산에 필요해요 →"}
            </CustomTypography>
          </Box>
        )}

        {/* 링크 생성은 계약 완료 이후에만 — 그 전에는 만들 수 있다는 것만 간접 안내 */}
        {result && result.codes.length === 0 && contractCompleted && (
          <CreateLinkSection onCreated={() => refetch()} />
        )}
        {result &&
          result.codes.length === 0 &&
          contractData?.code === 0 &&
          !contractCompleted && (
            <Stack
              spacing={"4px"}
              sx={{
                mt: 3,
                borderRadius: "16px",
                backgroundColor: theme.vars.palette.Surface.Forward,
                p: "20px",
              }}
            >
              <CustomTypography
                variant="Body"
                sx={{
                  fontWeight: 700,
                  color: theme.vars.palette.Content.Strong,
                }}
              >
                {"내 링크 만들기"}
              </CustomTypography>
              <CustomTypography
                variant="Footnote"
                sx={{
                  fontWeight: 500,
                  color: theme.vars.palette.Content.Weak,
                }}
              >
                {"계약이 완료되면 공유할 나만의 링크를 만들 수 있어요."}
              </CustomTypography>
            </Stack>
          )}

        {result && (
          <>
            <Stack direction="row" spacing={"12px"} sx={{ mt: 3 }}>
              <SummaryCard label="총 가입자" value={result.total.signupCount} />
              {/* 유효 결제자 = 결제자 − 환불자. 환불자 별도 카드는 두지 않는다 */}
              <SummaryCard
                label="총 유효 결제자"
                value={result.total.payerCount - result.total.refundCount}
                help="결제 후 환불한 사용자를 제외한 결제자 수예요."
              />
              {result.total.settlementAmount != null && (
                <SummaryCard
                  label="총 정산액"
                  value={result.total.settlementAmount}
                  unit="원"
                />
              )}
            </Stack>

            <Box sx={{ mt: 4 }}>
              <TabButtonList
                selectedTab={tab}
                data={[
                  {
                    id: "daily",
                    label: "일별",
                    onClick: () => setTab("daily"),
                  },
                  {
                    id: "monthly",
                    label: "월별",
                    onClick: () => setTab("monthly"),
                  },
                ]}
              />
            </Box>

            {/* 모바일에서 칼럼 헤더가 줄바꿈되지 않도록 — 최소 폭을 확보하고 가로 스크롤로 처리 */}
            <Box
              sx={{
                mt: "12px",
                borderRadius: "16px",
                backgroundColor: theme.vars.palette.Surface.Forward,
                p: 1,
                overflowX: "auto",
              }}
            >
              <Stack sx={{ minWidth: showSettlement ? "560px" : "420px" }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  sx={{ height: "40px", px: "12px" }}
                >
                  <StatCell flex={1.2}>
                    <CustomTypography
                      variant="Caption"
                      sx={{
                        fontWeight: 500,
                        color: theme.vars.palette.Content.Weak,
                      }}
                    >
                      {tab === "daily" ? "날짜" : "월"}
                    </CustomTypography>
                  </StatCell>
                  {[
                    {
                      label: "가입자",
                      help: "링크로 유입되어 코드가 연결된 사용자 수예요.",
                    },
                    {
                      label: "결제자",
                      help: "링크 진입 3일 내 결제로 기여가 확정된 사용자 수예요.",
                    },
                    {
                      label: "환불자",
                      help: "결제자 중 결제를 취소한 사용자 수예요.",
                    },
                    // 유효 결제자 칼럼은 월별 탭에만 — 그 달의 (결제자 − 환불자)
                    ...(tab === "monthly"
                      ? [
                          {
                            label: "유효 결제자",
                            help: "환불자를 제외한 결제자 수예요.",
                          },
                        ]
                      : []),
                  ].map(({ label, help }) => (
                    <StatCell key={label} flex={1} alignRight>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                      >
                        <CustomTypography
                          variant="Caption"
                          sx={{
                            fontWeight: 500,
                            color: theme.vars.palette.Content.Weak,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {label}
                        </CustomTypography>
                        <InfoTip help={help} />
                      </Box>
                    </StatCell>
                  ))}
                  {showSettlement && (
                    <StatCell flex={1.2} alignRight>
                      <CustomTypography
                        variant="Caption"
                        sx={{
                          fontWeight: 500,
                          color: theme.vars.palette.Content.Weak,
                        }}
                      >
                        {"정산액"}
                      </CustomTypography>
                    </StatCell>
                  )}
                </Stack>

                {buckets.length === 0 && (
                  <CustomTypography
                    variant="Caption"
                    sx={{
                      py: "24px",
                      textAlign: "center",
                      fontWeight: 500,
                      color: theme.vars.palette.Content.Weak,
                    }}
                  >
                    {"아직 집계된 데이터가 없어요"}
                  </CustomTypography>
                )}
                {buckets.map((bucket) => (
                  <Stack
                    key={bucket.period}
                    direction="row"
                    alignItems="center"
                    sx={{ height: "48px", px: "12px" }}
                  >
                    <StatCell flex={1.2}>
                      <CustomTypography
                        variant="Body"
                        sx={{
                          fontWeight: 500,
                          color: theme.vars.palette.Content.Medium,
                        }}
                      >
                        {bucket.period}
                      </CustomTypography>
                    </StatCell>
                    {[
                      bucket.signupCount,
                      bucket.payerCount,
                      bucket.refundCount,
                      ...(tab === "monthly"
                        ? [bucket.payerCount - bucket.refundCount]
                        : []),
                    ].map((count, index) => (
                      <StatCell key={index} flex={1} alignRight>
                        <CustomTypography
                          variant="Body"
                          sx={{
                            fontWeight: 500,
                            color: theme.vars.palette.Content.Strong,
                          }}
                        >
                          {count}
                        </CustomTypography>
                      </StatCell>
                    ))}
                    {showSettlement && (
                      <StatCell flex={1.2} alignRight>
                        <CustomTypography
                          variant="Body"
                          sx={{
                            fontWeight: 700,
                            color: theme.vars.palette.Content.Strong,
                          }}
                        >
                          {bucket.settlementAmount != null
                            ? `${bucket.settlementAmount.toLocaleString()}원`
                            : "-"}
                        </CustomTypography>
                      </StatCell>
                    )}
                  </Stack>
                ))}
              </Stack>
            </Box>

            {/* 활동 참고 자료 — 공식 채널·프로그램 소개. 오픈 여부 미정이라 일단 숨김 */}
            <Stack
              spacing={"12px"}
              sx={{
                display: "none",
                mt: 3,
                borderRadius: "16px",
                backgroundColor: theme.vars.palette.Surface.Forward,
                p: "20px",
              }}
            >
              <CustomTypography
                variant="Body"
                sx={{
                  fontWeight: 700,
                  color: theme.vars.palette.Content.Strong,
                }}
              >
                {"활동에 참고해 보세요"}
              </CustomTypography>
              {[
                {
                  title: "디스턴싱 공식 유튜브",
                  description:
                    "공식 채널의 영상을 콘텐츠 제작에 참고할 수 있어요.",
                  url: "https://www.youtube.com/@distancing",
                },
                {
                  title: "디스턴싱 프로그램 소개",
                  description:
                    "프로그램의 개념과 구성을 이해하는 데 도움이 돼요.",
                  url: "https://orwell.distancing.im/program",
                },
              ].map(({ title, description, url }) => (
                <Box
                  key={url}
                  onClick={() => window.open(url)}
                  sx={{ cursor: "pointer" }}
                >
                  <CustomTypography
                    variant="Caption"
                    sx={{
                      fontWeight: 700,
                      color: theme.vars.palette.Content.Strong,
                      textDecoration: "underline",
                    }}
                  >
                    {`${title} →`}
                  </CustomTypography>
                  <CustomTypography
                    variant="Footnote"
                    sx={{
                      fontWeight: 500,
                      color: theme.vars.palette.Content.Weak,
                    }}
                  >
                    {description}
                  </CustomTypography>
                </Box>
              ))}
            </Stack>
          </>
        )}

        {/* 푸터 — 로고를 은은하게, mt auto 로 항상 하단에 고정 */}
        <Box
          sx={{ display: "flex", justifyContent: "center", mt: "auto", pt: 6 }}
        >
          <img
            src={distancingLogo}
            alt="distancing logo"
            style={{ width: "80px", height: "auto", opacity: 0.35 }}
          />
        </Box>
      </Box>
    </Box>
  );
}

function StatCell({
  flex,
  alignRight,
  children,
}: {
  flex: number;
  alignRight?: boolean;
  children: ReactNode;
}) {
  return (
    <Box
      sx={{
        flex,
        display: "flex",
        justifyContent: alignRight ? "flex-end" : "flex-start",
      }}
    >
      {children}
    </Box>
  );
}

// 셀프 링크 생성 — 1인 1코드. 생성 후 대시보드를 다시 불러와 링크 목록을 갱신한다.
function CreateLinkSection({ onCreated }: { onCreated: () => void }) {
  const [slug, setSlug] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  // 생성 전 확인 모달 — 링크 주소는 만든 후 변경 불가라 한 번 더 확인시킨다
  const [confirming, setConfirming] = useState(false);

  const normalized = slug.trim().toLowerCase();
  const slugValid = CODE_SLUG_REGEX.test(normalized);
  const valid = slugValid;

  const create = async () => {
    if (!valid || submitting) return;
    setSubmitting(true);
    setMessage(null);
    try {
      const res = await ambassadorApi.createCode(normalized);
      if (res.code === 0) {
        setConfirming(false);
        onCreated();
        return;
      }
      setConfirming(false);
      setMessage(res.message || FALLBACK_ERROR_MESSAGE);
    } catch (e) {
      setConfirming(false);
      setMessage(FALLBACK_ERROR_MESSAGE);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Stack
      spacing={"12px"}
      sx={{
        mt: 3,
        borderRadius: "16px",
        backgroundColor: theme.vars.palette.Surface.Forward,
        p: "20px",
      }}
    >
      <Stack spacing={"4px"}>
        <CustomTypography
          variant="Body"
          sx={{ fontWeight: 700, color: theme.vars.palette.Content.Strong }}
        >
          {"내 링크 만들기"}
        </CustomTypography>
        <CustomTypography
          variant="Footnote"
          sx={{ fontWeight: 500, color: theme.vars.palette.Content.Weak }}
        >
          {
            "나만의 링크를 만드세요. 영문 소문자·숫자·하이픈(-)·언더스코어(_) 3~20자."
          }
        </CustomTypography>
      </Stack>
      {/* 입력창에는 코드만 받고, 완성될 주소는 입력창 위에 실시간 미리보기로 보여준다 */}
      <Stack spacing={"8px"}>
        <CustomTypography
          variant="Caption"
          sx={{
            fontWeight: 500,
            color: theme.vars.palette.Content.Strong,
            wordBreak: "break-all",
          }}
        >
          {`링크: ${window.location.host}/with/${slug || "코드"}`}
        </CustomTypography>
        <Input
          placeholder="코드 입력 (예: mycode)"
          value={slug}
          // 입력을 막지 않고 그대로 받는다 — 형식 오류는 아래 안내 문구로 보여준다 (대문자는 제출 시 소문자 변환)
          onChange={(e) => setSlug(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && valid && setConfirming(true)}
          sx={{
            // 로그인/계약 페이지와 동일한 입력 표준 (PromotionBookPhoneNumberSubmit 선례)
            width: "100%",
            height: "54px",
            borderRadius: "12px",
            backgroundColor: theme.vars.palette.Surface.Forward,
            fontSize: "16px",
            borderColor: "background.level3",
            borderWidth: "1px",
            "--joy-focus-thickness": "0px",
            boxShadow: "0px 0px 0px transparent",
            "&:focus-within": {
              boxShadow: `inset 0 0 0 1.333px ${theme.vars.palette.Content.Highlight}`,
            },
          }}
        />
        {/* 변경 불가 경고 — 인풋 바로 아래에 강조해서 보여준다 */}
        <CustomTypography
          variant="Footnote"
          sx={{ fontWeight: 700, color: theme.vars.palette.text.orange }}
        >
          {"*링크는 1개월 동안 변경할 수 없으니 신중히 만들어 주세요."}
        </CustomTypography>
        <Button
          variant="Action"
          size="sm"
          loading={submitting}
          disabled={!valid}
          onClick={() => setConfirming(true)}
          sx={{
            alignSelf: "flex-end",
            fontWeight: 700,
            height: "40px",
            minHeight: "40px",
            fontSize: "14px",
          }}
        >
          {"만들기"}
        </Button>
      </Stack>

      {/* 생성 전 확인 모달 */}
      {confirming && (
        <CustomModal
          open
          onClose={() => setConfirming(false)}
          title="이 주소로 링크를 만들까요?"
          subtitle="링크는 만든 후 1개월 동안 변경할 수 없어요."
          actions={[
            {
              label: "만들기",
              onClick: create,
              variant: "Solid",
              buttonProps: { loading: submitting },
            },
            {
              label: "취소",
              onClick: () => setConfirming(false),
              variant: "Outlined",
            },
          ]}
        >
          <CustomTypography
            variant="Body"
            sx={{
              fontWeight: 700,
              color: theme.vars.palette.Content.Strong,
              wordBreak: "break-all",
            }}
          >
            {`${window.location.host}/with/${normalized}`}
          </CustomTypography>
        </CustomModal>
      )}
      {slug.length > 0 && !slugValid && (
        <CustomTypography
          variant="Footnote"
          sx={{
            fontWeight: 500,
            color: theme.vars.palette.Content.HighlightWithAlert,
          }}
        >
          {
            "영문 소문자·숫자·하이픈·언더스코어만 사용해 3~20자로 입력해 주세요."
          }
        </CustomTypography>
      )}
      {message && (
        <CustomTypography
          variant="Caption"
          sx={{
            fontWeight: 500,
            color: theme.vars.palette.Content.HighlightWithAlert,
          }}
        >
          {message}
        </CustomTypography>
      )}
    </Stack>
  );
}

// 도움말 ⓘ 아이콘 — 툴팁을 제어 모드로: 기본 동작은 hover/길게누르기라 모바일에서 발견이 어렵다.
// 탭(클릭)으로 토글하고, 모바일에서 닫을 수단이 마땅치 않으니 4초 후 자동으로 닫는다.
// hover 핸들러는 달지 않는다 — 모바일 탭이 합성 mouseenter(열림)→click(토글 닫힘)으로
// 이어져 첫 탭에 떴다가 바로 사라지는 문제가 생긴다.
function InfoTip({ help }: { help: string }) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => setOpen(false), 4000);
    return () => clearTimeout(timer);
  }, [open]);

  return (
    <Tooltip title={help} placement="top" arrow open={open}>
      <InfoOutlinedIcon
        onClick={() => setOpen((v) => !v)}
        sx={{
          fontSize: "14px",
          color: theme.vars.palette.Content.Weak,
          cursor: "help",
        }}
      />
    </Tooltip>
  );
}

function SummaryCard({
  label,
  value,
  unit = "명",
  help,
}: {
  label: string;
  value: number;
  unit?: string;
  help?: string;
}) {
  return (
    <Stack
      spacing={"4px"}
      sx={{
        flex: 1,
        borderRadius: "16px",
        backgroundColor: theme.vars.palette.Surface.Forward,
        px: "16px",
        py: "20px",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
        <CustomTypography
          variant="Caption"
          sx={{ fontWeight: 500, color: theme.vars.palette.Content.Weak }}
        >
          {label}
        </CustomTypography>
        {help && <InfoTip help={help} />}
      </Box>
      <CustomTypography
        variant="H3"
        sx={{ fontWeight: 700, color: theme.vars.palette.Content.Strong }}
      >
        {`${value.toLocaleString()}${unit}`}
      </CustomTypography>
    </Stack>
  );
}

// 계정 관리 메뉴 — 프로필 정보 · 계약 정보 · 로그아웃 (비밀번호 변경은 알림톡 인증 기반 재설정으로 통합 예정이라 제외)
function AccountMenu({
  onLink,
  onContract,
  onLogout,
}: {
  onLink: () => void;
  onContract: () => void;
  onLogout: () => void;
}) {
  const [open, setOpen] = useState(false);

  const menuItemSx = {
    justifyContent: "flex-start",
    fontWeight: 500,
    color: theme.vars.palette.Content.Medium,
  };

  return (
    <Box sx={{ position: "relative", flexShrink: 0 }}>
      {/* 계정 관리 — 아이콘 트리거 */}
      <PersonIcon
        titleAccess="계정 관리"
        onClick={() => setOpen(!open)}
        sx={{
          fontSize: "24px",
          color: theme.vars.palette.Content.Weak,
          cursor: "pointer",
        }}
      />
      {open && (
        // 빈 공간(오버레이) 클릭 시 메뉴 닫기 — 메뉴(zIndex 10)보다 아래에 깔린다
        <Box
          onClick={() => setOpen(false)}
          sx={{ position: "fixed", inset: 0, zIndex: 9 }}
        />
      )}
      {open && (
        <Stack
          spacing={"2px"}
          sx={{
            position: "absolute",
            right: 0,
            zIndex: 10,
            mt: 1,
            width: "224px",
            borderRadius: "16px",
            backgroundColor: theme.vars.palette.Surface.Forward,
            boxShadow: theme.shadow.md,
            p: 1,
          }}
        >
          <Button
            variant="Plain"
            size="sm"
            sx={menuItemSx}
            onClick={() => {
              setOpen(false);
              onLink();
            }}
          >
            {"프로필 정보"}
          </Button>
          <Button
            variant="Plain"
            size="sm"
            sx={menuItemSx}
            onClick={() => {
              setOpen(false);
              onContract();
            }}
          >
            {"계약 정보"}
          </Button>
          <Button
            variant="Plain"
            size="sm"
            sx={{
              ...menuItemSx,
              color: theme.vars.palette.Content.HighlightWithAlert,
            }}
            onClick={onLogout}
          >
            {"로그아웃"}
          </Button>
        </Stack>
      )}
    </Box>
  );
}
